<script setup lang="tsx">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EditDeleteButtons from '../widgets/EditDeleteButtons.vue'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import InstanceSelect from '../widgets/InstanceSelect.vue'
import AssetConnections from './AssetConnections.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import type { CustomFieldDefinition, Asset, Response, AssetWithConnections, RiskShort } from '@/models/api-generated'
import { assetRefreshCallback } from '@/components/business_processes/shared'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import {
	AssetReference,
	PermAllAssetDelete,
	PermAllAssetWrite,
	PermOwnAssetDelete,
	PermOwnAssetWrite,
	RT_Exclude,
} from '@/models/enums-generated'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import CustomValueDisplay from '../custom_field/CustomValueDisplay.vue'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import { defaultInstance, genRouteID, hasPermissionOwn } from '@/services/utils'
import NumericInput from '../widgets/NumericInput.vue'
import URLReference from '../comment_references/URLReference.vue'
import DocumentReference from '../comment_references/DocumentReference.vue'
import { viewing } from '@/services/utils'
import { scoreToLevel } from '../risk_management/shared'
import Modal from '@/components/shared/Modal.vue'

const defaultAsset: Asset = {
	id: 0,
	inventoryNr: '',
	name: '',
	location: '',
	instance: defaultInstance(),
	price: 0,
	comment: '',
	status: true,
	customFields: {},
	isConnected: false,
	isChecked: false,
	urlReferences: [],
	documentReferences: [],
	isExpanded: false,
	subAssets: [],
	risks: [],
}

const defaultAssetConnections: AssetWithConnections = {
	asset: defaultAsset,
	businessProcesses: [],
	dataAssets: [],
	modules: [],
}

const props = defineProps<{ assetId: number }>()
const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const customValues = ref<CustomValueMap>()
const isAdd = computed(() => props.assetId === 0)
const isEdit = ref(false)
const model = ref<AssetWithConnections>({ ...defaultAssetConnections })
const isChangeMode = computed(() => isAdd.value || isEdit.value)
const canEdit = computed(() => hasPermissionOwn(model.value.asset.responsible, PermAllAssetWrite, PermOwnAssetWrite))
const canDelete = computed(() => hasPermissionOwn(model.value.asset.responsible, PermAllAssetDelete, PermOwnAssetDelete))
const canEditInstance = computed(() => viewing.value === 0)
const isValidInstance = computed(() => viewing.value !== 0 || model.value.asset.instance?.id > 0)
const isValidName = computed(() => model.value.asset.name.trim().length > 0)
const canSave = computed(() => isValidName.value && isValidInstance.value)
const custom = ref<CustomFieldDefinition[]>([])
const selectedModule = ref()
const selectedBP = ref()

onMounted(async () => {
	await refresh()
	slider.watchChanges(model)
})

watch(() => props.assetId, refresh)
watch(isEdit, (edit) => slider.setFooter(edit ? editBtns : viewBtns))
watch(selectedModule, (newModule) => {
	if (newModule && !model.value.modules.some((a) => a.id === newModule.id)) {
		const match = newModule.name.match(/\(([^)]+)\)/)
		newModule.name = match ? match[1].trim() : newModule.name
		model.value.modules.push(newModule)
		selectedModule.value = null
	}
})
watch(selectedBP, (newBP) => {
	if (newBP && !model.value.businessProcesses.some((bp) => bp.id === newBP.id)) {
		model.value.businessProcesses.push(newBP)
		selectedBP.value = null
	}
})

const editBtns = <CancelSaveButtons saveId="save-asset" cancelId="cancel-asset" canSave={canSave} onCancel={cancel} onSave={save} />
const viewBtns = (
	<EditDeleteButtons
		editId="open-edit-asset"
		deleteId="delete-asset"
		canEdit={canEdit}
		onEdit={edit}
		canDelete={canDelete}
		onDelete={remove}
	/>
)

async function edit() {
	isEdit.value = true
}

async function refresh() {
	isEdit.value = false
	if (!isAdd.value) {
		const { data } = await http.get<Response<AssetWithConnections>>(`/api/asset/${props.assetId}`)
		if (data.data) model.value = data.data
		slider.setFooter(viewBtns)
	} else {
		model.value = defaultAssetConnections
		slider.setFooter(editBtns)
		if (viewing.value > 0) {
			model.value.asset.instance.id = viewing.value
		}
	}
	await getCustomFields()
}

async function getCustomFields() {
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/assets`)
	if (data.data?.length) custom.value.push(...data.data)

	if (!isAdd.value) customValues.value = await fetchCustomValues('assets', props.assetId)
}

async function cancel() {
	const doCancel = async () => {
		if (isAdd.value) {
			slider.close()
		} else {
			await refresh()
		}
	}

	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		await doCancel()
	}
}

const showLinkedRiskModal = ref(false)

function remove() {
	if (assetConnectionRisks.value.length > 0) {
		showLinkedRiskModal.value = true
		return
	}

	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'asset', confirmDelete)
}

async function confirmDelete() {
	const { status } = await http.get(`/api/asset/delete/${genRouteID(model.value.asset.id, '', 'asset')}`)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyDeleted'))
		slider.setUnsavedChanges(false)
		slider.close()
		assetRefreshCallback.value?.forEach((fn) => fn())
	}
}

async function save() {
	const { status } = await http.post<Response<Asset>>('/api/asset', model.value.asset)
	if (status === 200) {
		toaster.showSuccess(t(isAdd.value ? 'misc.successfullyAdded' : 'misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		assetRefreshCallback.value?.forEach((fn) => fn())
	}
}

function updateCustomValue(fieldId: number, value: string) {
	customValues.value ??= {}
	customValues.value[fieldId] ??= {}
	customValues.value[fieldId][model.value.asset.id] = value
	model.value.asset.customFields ||= {}
	model.value.asset.customFields[fieldId] ??= {}
	model.value.asset.customFields[fieldId][model.value.asset.id] = value
}

const defaultConnectionRisks = computed(() => {
	const assetId = model.value.asset.id

	return model.value.asset.risks.filter((r) => !r.assetId || r.assetId !== assetId)
})

const assetConnectionRisks = computed(() => {
	const assetId = model.value.asset.id

	return model.value.asset.risks.filter((r) => r.assetId === assetId)
})

function goToRisks(names: string[]) {
	if (viewing.value === 0) return
	const params = new URLSearchParams()
	names.forEach((name) => {
		params.append('name', name)
	})
	window.location.href = `/app/risk-management?${params}`
}

const riskColor = (risk: RiskShort) => {
	return risk.treatment === RT_Exclude ? 'gray' : scoreToLevel(risk.residual)?.color || 'gray'
}
</script>

<template>
	<div :class="{ striped: !isChangeMode }">
		<div class="row">
			<label class="col-4 col-form-label">{{ t('asset.inventoryNr') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<input v-if="isChangeMode" data-test="asset-inventory-nr" class="form-control mb-2" v-model="model.asset.inventoryNr" required />
				<span v-else class="text-end">{{ model.asset.inventoryNr || '-' }}</span>
			</div>
		</div>
		<div class="row">
			<label class="col-4 col-form-label">{{ t('misc.name') }}</label>
			<div class="col-8 d-flex flex-column">
				<input
					v-if="isChangeMode"
					data-test="asset-name"
					class="form-control mb-1 w-100"
					v-model="model.asset.name"
					:class="{ 'is-invalid': !isValidName }"
					required
				/>
				<div v-if="isChangeMode" class="invalid-feedback">{{ $t('misc.validName') }}</div>
				<div v-else class="d-flex align-items-center justify-content-end flex-grow-1">
					<span class="text-end">{{ model.asset.name || '-' }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('responsible') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<ResponsibleSelect
					v-if="isChangeMode"
					v-model="model.asset.responsible"
					data-test="asset-responsible"
					class="mb-2"
				></ResponsibleSelect>
				<span v-else class="text-end">{{ model.asset.responsible?.label || '-' }}</span>
			</div>
		</div>

		<div v-if="canEditInstance" class="row">
			<label class="col-4 col-form-label">{{ t('institution') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<div v-if="isChangeMode" class="w-100">
					<InstanceSelect
						v-model="model.asset.instance"
						data-test="asset-instance"
						:class="{ 'is-invalid': !isValidInstance }"
						class="mb-2 w-100"
					/>
					<div class="invalid-feedback">{{ $t('misc.validInstance') }}</div>
				</div>
				<span v-else class="text-end">{{ model.asset.instance?.name || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('location') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<input v-if="isChangeMode" class="form-control mb-2" v-model="model.asset.location" data-test="asset-location" />
				<span v-else class="text-end">{{ model.asset.location || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('price') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<NumericInput
					v-if="isChangeMode"
					data-test="asset-price"
					class="form-control input-box mb-1"
					v-model="model.asset.price"
				></NumericInput>
				<span v-else class="text-end">{{ model.asset.price || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label for="asset-comment" class="col-4 col-form-label">{{ t('misc.comment') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<textarea
					v-if="isChangeMode"
					v-model="model.asset.comment"
					class="form-control w-100 mb-1"
					name="comment"
					data-test="asset-comment"
					maxlength="400"
				></textarea>
				<span v-else class="text-end w-100">{{ model.asset.comment || '-' }}</span>
			</div>
		</div>

		<template v-if="!isChangeMode">
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex justify-content-end align-items-center text-break">
						<CustomValueDisplay :field="field" :value="customValues?.[field.id]?.[model.asset.id] ?? ''" />
					</div>
				</div>
			</template>
		</template>
		<template v-else>
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex justify-content-end align-items-center text-break">
						<CustomValueEdit
							:field="field"
							:modelValue="customValues?.[field.id]?.[model.asset.id] ?? ''"
							@update:modelValue="(val) => updateCustomValue(field.id, val ?? '')"
						/>
					</div>
				</div>
			</template>
		</template>
		<div class="row">
			<label class="col-4 col-form-label">{{ t('status.label') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<div v-if="isChangeMode" class="form-check form-switch">
					<input data-test="status" type="checkbox" class="form-check-input" v-model="model.asset.status" />
				</div>
				<div v-else>
					<i v-if="model.asset.status" class="bi bi-circle-fill status-normal-background"></i>
					<i v-else class="bi bi-circle-fill status-verybig-background"></i>
				</div>
			</div>
		</div>
	</div>
	<div>
		<div class="col">
			<h6>{{ $t('reference.label', 2) }}</h6>
		</div>
	</div>

	<hr class="mt-2 mb-2 border-1" />
	<div class="row">
		<div class="col-5">
			<URLReference
				v-model="model.asset.urlReferences"
				:owner-type="AssetReference"
				:owner-id="model.asset.id"
				:can-edit="isEdit || isAdd"
			/>
		</div>

		<div class="col-auto d-flex">
			<div class="vr mx-3"></div>
		</div>

		<div class="col-5">
			<DocumentReference
				v-model="model.asset.documentReferences"
				:owner-type="AssetReference"
				:owner-id="model.asset.id"
				:can-edit="isEdit || isAdd"
			/>
		</div>
	</div>
	<hr class="mt-2 mb-2 border-1" />
	<div class="row">
		<div class="col-5">
			<h6>{{ t('asset.risks.default') }}</h6>

			<ul class="list-unstyled">
				<li v-for="risk in defaultConnectionRisks" :key="risk.metaId" class="d-flex align-items-center">
					<i class="bi bi-circle-fill me-2" :style="{ color: riskColor(risk) }"></i>
					<i v-if="risk.treatment === RT_Exclude" class="bi bi-x position-absolute text-white"></i>
					<span
						class="text-truncate text-nowrap overflow-hidden d-inline-block risks-cell"
						v-one-tooltip="risk.name"
						@click="goToRisks([risk.name])"
					>
						{{ risk.name }}
					</span>
				</li>
			</ul>
		</div>

		<div class="col-auto d-flex">
			<div class="vr mx-3"></div>
		</div>

		<div class="col-5">
			<h6>{{ t('asset.risks.connection') }}</h6>
			<ul class="list-unstyled">
				<li v-for="risk in assetConnectionRisks" :key="risk.metaId" class="d-flex align-items-center">
					<i class="bi bi-circle-fill me-2" :style="{ color: riskColor(risk) }"></i>
					<i v-if="risk.treatment === RT_Exclude" class="bi bi-x position-absolute text-white"></i>
					<span
						class="text-truncate text-nowrap overflow-hidden d-inline-block risks-cell"
						v-one-tooltip="risk.name"
						@click="goToRisks([risk.name])"
					>
						{{ risk.name }}
					</span>
				</li>
			</ul>
		</div>
	</div>

	<AssetConnections
		:can-edit="canEdit"
		:is-change-mode="isChangeMode"
		v-model="model"
		@cancel-edit="slider.setFooter(viewBtns)"
	></AssetConnections>

	<Modal v-model="showLinkedRiskModal" type="asset-linked-risks">
		<template #header>
			<h5>{{ t('asset.risks.blockTitle') }}</h5>
		</template>
		<template #body>
			<p>{{ t('asset.risks.blockMessage') }}</p>
			<ul class="list-unstyled mb-0">
				<li v-for="risk in assetConnectionRisks" :key="risk.metaId" class="d-flex align-items-center">
					<i class="bi bi-circle-fill me-2" :style="{ color: riskColor(risk) }"></i>
					<i v-if="risk.treatment === RT_Exclude" class="bi bi-x position-absolute text-white"></i>
					<span class="risks-cell" @click="goToRisks([risk.name])">{{ risk.name }}</span>
				</li>
			</ul>
		</template>
		<template #footer>
			<button type="button" class="btn btn-secondary" @click="showLinkedRiskModal = false">{{ t('misc.close') }}</button>
		</template>
	</Modal>
</template>
